/**
 * Outreach API client.
 *
 * Phase 60-07 (D-03.1): file name retained per CONTEXT D-03.1 ("максимально просто и стабильно";
 * file rename out of Phase 60 scope). The funnel-CRUD/enroll/AI-verify exports were deleted with
 * the funnel layer; the surviving non-funnel exports (campaigns dropdown, custom variables) are
 * kept here verbatim, and four new campaign-direct exports replace the deleted funnel surface
 * for the 60-07 frontend rebind.
 *
 * Phase 89 W2 (CLEANUP-04): getEmailScripts/getEmailScript/createEmailScript/updateEmailScript/
 *   deleteEmailScript/previewEmailScript removed — EmailScript route + table dropped.
 * - getCampaignsForDropdown hits /outreach/campaigns (Phase 97.8 W2: all-campaigns, no provider filter).
 * - getCustomVariables hits /outreach/custom-variables.
 * - enrollLeadIntoCampaign / getCampaignLeads / removeLeadFromCampaign / getActiveCampaignMemberships
 *   are the campaign-direct replacements consumed by FunnelEnrollModal, EnrollmentPage, and
 *   CampaignLeadsTable. See 60-07-PLAN.md Task 1 + 60-CONTEXT.md D-03.1 / D-04.1.
 */
import apiClient from '../client';

// ── Campaigns (for step dropdown) ─────────────────────────────────────────

export async function getCampaignsForDropdown() {
  // Phase 97.8 W2: drop ?instantlyOnly=true (Reply.io is the only provider after cutover).
  const { data } = await apiClient.get('/outreach/campaigns');
  return data?.items || [];
}

// ── Custom Variables ──────────────────────────────────────────────────────

export async function getCustomVariables() {
  const { data } = await apiClient.get('/outreach/custom-variables');
  return data;
}

// ── Phase 60-07 (D-03.1): campaign-direct replacements for the deleted funnel surface ──
//
// These exports replace enrollLeads / getFunnelLeads / removeEnrollment / getActiveEnrollments
// 1-for-1 from the consumers' perspective. File name retained per D-03.1 — internals are
// rebound on /outreach/campaigns/* endpoints.

/**
 * POST /outreach/campaigns/stage-1/enroll — replaces enrollLeads(funnelId, ...).
 *
 * Backend `stage1Enroll` accepts `{ leadId, campaignId }` in body and runs the canonical
 * Phase 59-02 snapshot+rollback path (see backend/src/routes/outreach/campaigns.js:1852).
 * Caller passes campaignId + leadId; loop callers (FunnelEnrollModal, EnrollmentPage)
 * iterate one leadId at a time so per-lead errors don't poison the whole batch.
 *
 * The signature mirrors the old enrollLeads(funnelId, { leadIds }) shape but with single-lead
 * semantics — kept simple so consumers can drop the existing `for (const leadId of …)` loop
 * in place.
 */
export async function enrollLeadIntoCampaign(campaignId, { leadId }) {
  const { data } = await apiClient.post('/outreach/campaigns/stage-1/enroll', {
    campaignId,
    leadId,
  });
  return data;
}

/**
 * GET /outreach/campaigns/:campaignId/leads — replaces getFunnelLeads(funnelId, ...).
 *
 * Returns `{ leads: [...], managerOptions: [...] }` per backend campaigns.js:1453-1525.
 * Each row has flat OutreachRecipient JOIN Lead shape:
 *   { recipientId, leadId, leadName, instagram, email, managerId, managerName,
 *     status, currentStage, sent, sentAt, replied, repliedAt, lastActivity }
 * Consumed by CampaignLeadsTable.jsx (Phase 60-06 stub replaced here).
 */
export async function getCampaignLeads(campaignId, { managerFilter } = {}) {
  const params = managerFilter ? { managerFilter } : {};
  const { data } = await apiClient.get(`/outreach/campaigns/${campaignId}/leads`, { params });
  return data;
}

/**
 * DELETE /outreach/campaigns/stage-1/enroll — replaces removeEnrollment(enrollmentId).
 *
 * Backend takes `{ leadId, campaignId }` in the request body (recipientId is a frontend-only
 * row key; backend resolves the recipient via the (campaignId, leadId) UNIQUE constraint).
 * CampaignLeadsTable rows from /:campaignId/leads carry both recipientId AND leadId — the
 * caller must pass leadId so backend can locate the row.
 *
 * Per a planned Phase 62 cleanup, a per-recipientId DELETE endpoint may replace this — but
 * per CONTEXT D-01 ("максимально просто и стабильно") we reuse the existing endpoint here.
 */
export async function removeLeadFromCampaign(campaignId, recipientId, { leadId } = {}) {
  const { data } = await apiClient.delete('/outreach/campaigns/stage-1/enroll', {
    data: { campaignId, leadId },
  });
  return data;
}

/**
 * Phase 60-07 (D-03.1) STUB — replaces getActiveEnrollments(leadIds).
 *
 * The original purpose was a UX nicety in FunnelEnrollModal: gray out campaigns the lead is
 * already in. The Phase 41 (campaignId, leadId) UNIQUE constraint already prevents real
 * duplicate enrollment at the backend, so this is non-blocking.
 *
 * Phase 60 ships with this returning [] — full implementation is Phase 62 backlog
 * (threat T-60-07-02 "accept" disposition). Documented in 60-07-SUMMARY.
 */
export async function getActiveCampaignMemberships(_leadIds) {
  return [];
}
